/**
 * Read a zip archive's entries in the browser, without a dependency.
 *
 * The same trade the avatar reader made (DEC-2026-08-02-022): a hundred lines
 * of container parsing over a library, because the format needed here is a
 * narrow, stable slice — a Spotify privacy export is stored or deflated
 * entries, no encryption, no zip64 — and the platform already ships the only
 * hard part as `DecompressionStream('deflate-raw')`. Anything outside that
 * slice is refused by name rather than mishandled.
 */

const EOCD_SIG = 0x06054b50
const CENTRAL_SIG = 0x02014b50

/**
 * Parse the central directory. Returns entries as
 * `{ name, size, read(): Promise<Uint8Array> }` — nothing is decompressed
 * until asked, so listing a 300 MB archive costs nothing.
 */
export function readZip(arrayBuffer) {
  const view = new DataView(arrayBuffer)
  const eocd = findEndOfCentralDirectory(view)
  if (eocd === -1) throw new Error('Not a zip file (no central directory).')

  const count = view.getUint16(eocd + 10, true)
  let offset = view.getUint32(eocd + 16, true)
  const entries = []
  const names = new TextDecoder()
  for (let i = 0; i < count; i += 1) {
    if (view.getUint32(offset, true) !== CENTRAL_SIG) {
      throw new Error('Corrupt zip (central directory entry out of place).')
    }
    const method = view.getUint16(offset + 10, true)
    const compressedSize = view.getUint32(offset + 20, true)
    const size = view.getUint32(offset + 24, true)
    const nameLength = view.getUint16(offset + 28, true)
    const extraLength = view.getUint16(offset + 30, true)
    const commentLength = view.getUint16(offset + 32, true)
    const localOffset = view.getUint32(offset + 42, true)
    const name = names.decode(new Uint8Array(arrayBuffer, offset + 46, nameLength))
    entries.push({
      name,
      size,
      read: () => readEntry(arrayBuffer, view, { method, compressedSize, localOffset, name }),
    })
    offset += 46 + nameLength + extraLength + commentLength
  }
  return entries
}

function findEndOfCentralDirectory(view) {
  // The EOCD sits at the very end, behind at most a 64 KiB comment.
  const floor = Math.max(0, view.byteLength - 22 - 65535)
  for (let i = view.byteLength - 22; i >= floor; i -= 1) {
    if (view.getUint32(i, true) === EOCD_SIG) return i
  }
  return -1
}

async function readEntry(arrayBuffer, view, { method, compressedSize, localOffset, name }) {
  // The local header repeats the name and extra fields at its own lengths —
  // they can differ from the central directory's, so the data offset must be
  // computed from the local copy.
  const nameLength = view.getUint16(localOffset + 26, true)
  const extraLength = view.getUint16(localOffset + 28, true)
  const start = localOffset + 30 + nameLength + extraLength
  const data = new Uint8Array(arrayBuffer, start, compressedSize)
  if (method === 0) return data
  if (method === 8) {
    const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
    return new Uint8Array(await new Response(stream).arrayBuffer())
  }
  throw new Error(`${name}: unsupported zip compression (method ${method}).`)
}
